import { useState, useEffect, useCallback, useRef } from 'react';

interface TimerState {
  minutes: number;
  seconds: number;
  isRunning: boolean;
  remainingTime: number; // in seconds
}

interface UseTimerReturn {
  minutes: number;
  seconds: number;
  displayMinutes: string;
  displaySeconds: string;
  isRunning: boolean;
  isEnabled: boolean;
  setMinutes: (minutes: number) => void;
  setSeconds: (seconds: number) => void;
  start: () => void;
  stop: () => void;
  reset: () => void;
  setTime: (minutes: number, seconds: number) => void;
}

const TIMER_STORAGE_KEY = 'polytrome-timer-values';

// Load timer values from localStorage with default fallback
const loadTimerValues = (): { minutes: number; seconds: number } => {
  try {
    const stored = localStorage.getItem(TIMER_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        minutes: typeof parsed.minutes === 'number' ? Math.max(0, Math.min(59, parsed.minutes)) : 1,
        seconds: typeof parsed.seconds === 'number' ? Math.max(0, Math.min(59, parsed.seconds)) : 0,
      };
    }
  } catch (error) {
    console.warn('Failed to load timer values from localStorage:', error);
  }
  
  // Default to 1 minute 0 seconds
  return { minutes: 1, seconds: 0 };
};

// Save timer values to localStorage
const saveTimerValues = (minutes: number, seconds: number) => {
  try {
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify({ minutes, seconds }));
  } catch (error) {
    console.warn('Failed to save timer values to localStorage:', error);
  }
};

export const useTimer = (
  initialMinutes: number = 1,
  initialSeconds: number = 0,
  enabled: boolean = false,
  onTimerComplete?: () => void
): UseTimerReturn => {
  // Load initial values from localStorage
  const { minutes: storedMinutes, seconds: storedSeconds } = loadTimerValues();
  
  const [state, setState] = useState<TimerState>({
    minutes: storedMinutes,
    seconds: storedSeconds,
    isRunning: false,
    remainingTime: storedMinutes * 60 + storedSeconds,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onTimerCompleteRef = useRef(onTimerComplete);

  // Update the callback ref when it changes
  useEffect(() => {
    onTimerCompleteRef.current = onTimerComplete;
  }, [onTimerComplete]);

  // Update remaining time when minutes/seconds change
  useEffect(() => {
    if (!state.isRunning) {
      setState(prev => ({
        ...prev,
        remainingTime: prev.minutes * 60 + prev.seconds,
      }));
    }
  }, [state.minutes, state.seconds, state.isRunning]);

  // Timer countdown logic
  useEffect(() => {
    if (state.isRunning && state.remainingTime > 0) {
      intervalRef.current = setInterval(() => {
        setState(prev => {
          const newRemainingTime = prev.remainingTime - 1;
          
          if (newRemainingTime <= 0) {
            // Timer completed
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            
            // Call the completion callback
            if (onTimerCompleteRef.current) {
              onTimerCompleteRef.current();
            }
            
            return {
              ...prev,
              isRunning: false,
              remainingTime: 0,
            };
          }
          
          return {
            ...prev,
            remainingTime: newRemainingTime,
          };
        });
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state.isRunning, state.remainingTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const setMinutes = useCallback((minutes: number) => {
    const clampedMinutes = Math.max(0, Math.min(59, minutes));
    setState(prev => {
      const newState = {
        ...prev,
        minutes: clampedMinutes,
      };
      // Save to localStorage
      saveTimerValues(clampedMinutes, newState.seconds);
      return newState;
    });
  }, []);

  const setSeconds = useCallback((seconds: number) => {
    const clampedSeconds = Math.max(0, Math.min(59, seconds));
    setState(prev => {
      const newState = {
        ...prev,
        seconds: clampedSeconds,
      };
      // Save to localStorage
      saveTimerValues(newState.minutes, clampedSeconds);
      return newState;
    });
  }, []);

  const setTime = useCallback((minutes: number, seconds: number) => {
    const clampedMinutes = Math.max(0, Math.min(59, minutes));
    const clampedSeconds = Math.max(0, Math.min(59, seconds));
    setState(prev => {
      const newState = {
        ...prev,
        minutes: clampedMinutes,
        seconds: clampedSeconds,
        remainingTime: clampedMinutes * 60 + clampedSeconds,
      };
      // Save to localStorage
      saveTimerValues(clampedMinutes, clampedSeconds);
      return newState;
    });
  }, []);

  const start = useCallback(() => {
    if (enabled && state.remainingTime > 0) {
      setState(prev => ({
        ...prev,
        isRunning: true,
      }));
    }
  }, [enabled, state.remainingTime]);

  const stop = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRunning: false,
    }));
  }, []);

  const reset = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRunning: false,
      remainingTime: prev.minutes * 60 + prev.seconds,
    }));
  }, []);

  // Calculate display values from remaining time
  const displayMinutes = Math.floor(state.remainingTime / 60).toString().padStart(2, '0');
  const displaySeconds = (state.remainingTime % 60).toString().padStart(2, '0');

  return {
    minutes: state.minutes,
    seconds: state.seconds,
    displayMinutes,
    displaySeconds,
    isRunning: state.isRunning,
    isEnabled: enabled,
    setMinutes,
    setSeconds,
    start,
    stop,
    reset,
    setTime,
  };
};
